import {
  addMessage,
  buildChatContext,
  fetchChatSuggestions,
  loadSessions,
  newSession,
  sendChatMessageStream,
  submitChatFeedback,
  upsertSession,
  type ChatCard,
  type ChatLanguage,
  type ChatMessage,
  type ChatSession,
} from '../services/chat-ui';
import { trackChatFeedback, trackChatMessageReceived, trackChatMessageSent, trackLanguageSwitch } from '../analytics/plausible';
import { getLanguagePreference, setLanguagePreference } from '../router/guards';

let sessions: ChatSession[] = [];
let activeSessionId: string | null = null;
let detachHandlers: Array<() => void> = [];

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getActiveSession(): ChatSession {
  if (!activeSessionId) {
    const created = newSession('en');
    sessions = upsertSession(created);
    activeSessionId = created.id;
    return created;
  }

  const existing = sessions.find(item => item.id === activeSessionId);
  if (existing) return existing;

  const fallback = newSession('en');
  sessions = upsertSession(fallback);
  activeSessionId = fallback.id;
  return fallback;
}

function renderSessionList(): void {
  const list = document.getElementById('chat-session-list');
  if (!list) return;

  list.innerHTML = sessions
    .map(session => {
      const active = session.id === activeSessionId ? 'chat-page__session--active' : '';
      return `<button class="chat-page__session ${active}" data-chat-session="${session.id}">${escapeHtml(session.title)}</button>`;
    })
    .join('');
}

function cardText(card: ChatCard, key: string, fallback = ''): string {
  const value = card[key];
  return typeof value === 'string' ? value : fallback;
}

function cardNumber(card: ChatCard, key: string): number | null {
  const value = card[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function renderSingleCard(card: ChatCard): string {
  const type = cardText(card, 'type', 'insight');

  if (type === 'news') {
    const title = cardText(card, 'title', 'Headline');
    const source = cardText(card, 'source', 'unknown');
    const impact = cardText(card, 'impact', 'unknown');
    const score = cardNumber(card, 'score');
    return `<div class="chat-page__card"><div>${escapeHtml(title)}</div><small>${escapeHtml(source)} | impact ${escapeHtml(impact)}${score !== null ? ` | score ${score}` : ''}</small></div>`;
  }

  if (type === 'earnings') {
    const company = cardText(card, 'company', 'Company');
    const verdict = cardText(card, 'verdict', 'mixed');
    const score = cardNumber(card, 'score');
    const keyPoints = Array.isArray(card.keyPoints) ? card.keyPoints.slice(0, 2).map(item => String(item)).join(' | ') : '';
    return `<div class="chat-page__card"><div>${escapeHtml(company)} earnings</div><small>${escapeHtml(verdict)}${score !== null ? ` | score ${score.toFixed(1)}` : ''}${keyPoints ? ` | ${escapeHtml(keyPoints)}` : ''}</small></div>`;
  }

  if (type === 'sector') {
    const name = cardText(card, 'name', 'Sector');
    const changePct = cardNumber(card, 'changePct');
    const strength = cardText(card, 'strength', 'medium');
    const move = changePct === null ? '--' : `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`;
    return `<div class="chat-page__card"><div>${escapeHtml(name)}</div><small>${escapeHtml(move)} | ${escapeHtml(strength)}</small></div>`;
  }

  if (type === 'table') {
    const title = cardText(card, 'title', 'Table');
    const rows = Array.isArray(card.rows) ? card.rows : [];
    const rowText = rows.slice(0, 3)
      .map((row) => Array.isArray(row) ? row.slice(0, 2).map(cell => String(cell)).join(': ') : '')
      .filter(Boolean)
      .join(' | ');
    return `<div class="chat-page__card"><div>${escapeHtml(title)}</div><small>${escapeHtml(rowText)}</small></div>`;
  }

  if (type === 'impact') {
    const impact = cardText(card, 'impact', 'unknown');
    const category = cardText(card, 'category', 'general');
    return `<div class="chat-page__card"><div>Impact: ${escapeHtml(impact)}</div><small>${escapeHtml(category)}</small></div>`;
  }

  if (type === 'stock') {
    const symbol = cardText(card, 'symbol', 'STOCK');
    const price = cardNumber(card, 'price');
    const changePct = cardNumber(card, 'changePct');
    const side = cardText(card, 'side', 'flat');
    const priceText = price === null ? '--' : price.toFixed(2);
    const changeText = changePct === null ? '--' : `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`;
    return `<div class="chat-page__card"><div>${escapeHtml(symbol)} ${escapeHtml(priceText)}</div><small>${escapeHtml(changeText)} | ${escapeHtml(side)}</small></div>`;
  }

  if (type === 'chart') {
    const title = cardText(card, 'title', 'Chart');
    const latest = cardNumber(card, 'latest');
    const changePct = cardNumber(card, 'changePct');
    const timeframe = cardText(card, 'timeframe', '1D');
    const latestText = latest === null ? '--' : latest.toFixed(2);
    const move = changePct === null ? '--' : `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`;
    return `<div class="chat-page__card"><div>${escapeHtml(title)} ${escapeHtml(latestText)}</div><small>${escapeHtml(move)} | ${escapeHtml(timeframe)} mini chart</small></div>`;
  }

  const title = cardText(card, 'title', type);
  const subtitle = cardText(card, 'source', cardText(card, 'impact', ''));
  return `<div class="chat-page__card"><div>${escapeHtml(title)}</div><small>${escapeHtml(subtitle)}</small></div>`;
}

function renderCards(cards: ChatCard[] | undefined): string {
  if (!cards || cards.length === 0) return '';

  const items = cards.slice(0, 4).map(renderSingleCard).join('');

  return `<div class="chat-page__cards">${items}</div>`;
}

function setLanguageSelect(session: ChatSession): void {
  const select = document.getElementById('chat-language') as HTMLSelectElement | null;
  if (select) select.value = session.language;
}

function renderMessages(): void {
  const stream = document.getElementById('chat-message-stream');
  if (!stream) return;

  const session = getActiveSession();
  if (session.messages.length === 0) {
    stream.innerHTML = '<div class="chat-page__empty">Ask about market overview, top headlines, or why a move matters.</div>';
    return;
  }

  stream.innerHTML = session.messages
    .map((message: ChatMessage) => {
      const roleClass = message.role === 'user' ? 'chat-page__msg--user' : 'chat-page__msg--assistant';
      const controls = message.role === 'assistant'
        ? `<div class="chat-page__feedback"><button data-chat-feedback="up" data-chat-id="${message.id}">Useful</button><button data-chat-feedback="down" data-chat-id="${message.id}">Needs work</button></div>`
        : '';
      return `
        <article class="chat-page__msg ${roleClass}">
          <div class="chat-page__bubble">${escapeHtml(message.content)}</div>
          ${renderCards(message.cards)}
          ${controls}
        </article>
      `;
    })
    .join('');

  stream.scrollTop = stream.scrollHeight;
}

function setStatus(text: string): void {
  const node = document.getElementById('chat-status');
  if (node) node.textContent = text;
}

function renderSuggestions(suggestions: string[]): void {
  const node = document.getElementById('chat-suggestions');
  if (!node) return;
  node.innerHTML = suggestions.slice(0, 4).map(item => (`<button data-chat-suggest="${escapeHtml(item)}">${escapeHtml(item)}</button>`)).join('');
}

async function handleSend(raw: string): Promise<void> {
  const text = raw.trim();
  if (!text) return;

  let session = getActiveSession();
  session = addMessage(session, { role: 'user', content: text });
  sessions = upsertSession(session);
  activeSessionId = session.id;
  renderSessionList();
  renderMessages();
  setStatus('Thinking...');

  const streamRoot = document.getElementById('chat-message-stream');
  const streamArticle = document.createElement('article');
  streamArticle.className = 'chat-page__msg chat-page__msg--assistant';
  streamArticle.innerHTML = '<div class="chat-page__bubble chat-page__bubble--streaming"></div>';
  const streamBubble = streamArticle.querySelector('.chat-page__bubble') as HTMLElement | null;
  streamRoot?.appendChild(streamArticle);
  if (streamRoot) streamRoot.scrollTop = streamRoot.scrollHeight;

  try {
    const history = buildChatContext(session.messages);
    setStatus(history.length < session.messages.length ? 'Thinking... (context compacted)' : 'Thinking...');
    trackChatMessageSent('page', session.language);

    let liveText = '';
    let streamMeta: { intent?: string; cards?: ChatCard[]; suggestions?: string[] } = {};

    const response = await sendChatMessageStream({
      message: text,
      language: session.language,
      messages: history,
      onMeta: (meta) => {
        streamMeta = {
          intent: meta.intent,
          cards: meta.cards,
          suggestions: meta.suggestions,
        };
      },
      onToken: (token) => {
        liveText += token;
        if (streamBubble) streamBubble.textContent = liveText;
        if (streamRoot) streamRoot.scrollTop = streamRoot.scrollHeight;
      },
    });

    session = addMessage(session, {
      role: 'assistant',
      content: response.message || liveText || 'No response available.',
      intent: response.intent || streamMeta.intent,
      cards: response.cards || streamMeta.cards,
    });

    sessions = upsertSession(session);
    activeSessionId = session.id;
    renderSessionList();
    renderMessages();
    renderSuggestions(response.suggestions || streamMeta.suggestions || []);
    setStatus(`Streaming ${response.intent || streamMeta.intent || 'general'} response`);
    trackChatMessageReceived('page', response.intent || streamMeta.intent || 'general');
  } catch (err) {
    streamArticle.remove();
    session = addMessage(session, {
      role: 'assistant',
      content: `Chat unavailable: ${(err as Error).message}`,
    });
    sessions = upsertSession(session);
    renderMessages();
    setStatus('Degraded');
  }
}

export function renderChat(): string {
  return `
    <section class="chat-page">
      <aside class="chat-page__sidebar">
        <div class="chat-page__sidebar-head">
          <h3>Conversations</h3>
          <button id="chat-new-session" type="button">New</button>
        </div>
        <div id="chat-session-list" class="chat-page__sessions"></div>
      </aside>

      <main class="chat-page__main">
        <header class="chat-page__header">
          <div>
            <h2>IndiEye Chat</h2>
            <p id="chat-status">Ready</p>
          </div>
          <div class="chat-page__controls">
            <label for="chat-language">Lang</label>
            <select id="chat-language" aria-label="Chat language">
              <option value="en">EN</option>
              <option value="hi">HI</option>
              <option value="ta">TA</option>
              <option value="te">TE</option>
            </select>
          </div>
          <div id="chat-suggestions" class="chat-page__suggestions"></div>
        </header>

        <div id="chat-message-stream" class="chat-page__stream"></div>

        <form id="chat-form" class="chat-page__composer">
          <textarea id="chat-input" rows="3" placeholder="Ask about market setup, news, sectors, risk..."></textarea>
          <button type="submit">Send</button>
        </form>
      </main>
    </section>
  `;
}

export function initChat(): void {
  detachHandlers.forEach(fn => fn());
  detachHandlers = [];

  sessions = loadSessions();
  if (sessions.length === 0) {
    const created = newSession(getLanguagePreference() as ChatLanguage);
    sessions = upsertSession(created);
  }
  activeSessionId = sessions[0]?.id || null;

  renderSessionList();
  renderMessages();
  setLanguageSelect(getActiveSession());

  void fetchChatSuggestions().then(renderSuggestions);

  const form = document.getElementById('chat-form') as HTMLFormElement | null;
  const input = document.getElementById('chat-input') as HTMLTextAreaElement | null;
  const list = document.getElementById('chat-session-list');
  const suggest = document.getElementById('chat-suggestions');
  const newSessionBtn = document.getElementById('chat-new-session');
  const languageSelect = document.getElementById('chat-language') as HTMLSelectElement | null;
  const stream = document.getElementById('chat-message-stream');

  if (!form || !input || !list || !suggest || !newSessionBtn || !stream || !languageSelect) return;

  const submitHandler = (event: Event) => {
    event.preventDefault();
    const value = input.value;
    input.value = '';
    void handleSend(value);
  };

  const sessionHandler = (event: Event) => {
    const target = event.target as HTMLElement;
    const button = target.closest('[data-chat-session]') as HTMLElement | null;
    if (!button) return;
    const sessionId = button.dataset.chatSession;
    if (!sessionId) return;
    activeSessionId = sessionId;
    renderSessionList();
    renderMessages();
    setLanguageSelect(getActiveSession());
  };

  const suggestionHandler = (event: Event) => {
    const target = event.target as HTMLElement;
    const button = target.closest('[data-chat-suggest]') as HTMLElement | null;
    if (!button) return;
    const prompt = button.dataset.chatSuggest || '';
    if (prompt) {
      input.value = prompt;
      input.focus();
    }
  };

  const feedbackHandler = (event: Event) => {
    const target = event.target as HTMLElement;
    const button = target.closest('[data-chat-feedback]') as HTMLElement | null;
    if (!button) return;
    const rating = button.dataset.chatFeedback;
    const messageId = button.dataset.chatId;
    if ((rating === 'up' || rating === 'down') && messageId) {
      void submitChatFeedback(messageId, rating);
      setStatus('Feedback captured');
      trackChatFeedback(rating);
    }
  };

  const newSessionHandler = () => {
    const created = newSession(languageSelect.value as ChatLanguage);
    sessions = upsertSession(created);
    activeSessionId = created.id;
    renderSessionList();
    renderMessages();
    setLanguageSelect(created);
    input.focus();
  };

  const languageHandler = () => {
    const session = getActiveSession();
    const next = languageSelect.value as ChatLanguage;
    if (session.language === next) return;

    trackLanguageSwitch(session.language, next);
    setLanguagePreference(next);

    const updated = {
      ...session,
      language: next,
    };
    sessions = upsertSession(updated);
    activeSessionId = updated.id;
    setStatus(`Language set to ${next.toUpperCase()}`);
  };

  form.addEventListener('submit', submitHandler);
  list.addEventListener('click', sessionHandler);
  suggest.addEventListener('click', suggestionHandler);
  stream.addEventListener('click', feedbackHandler);
  newSessionBtn.addEventListener('click', newSessionHandler);
  languageSelect.addEventListener('change', languageHandler);

  detachHandlers.push(() => form.removeEventListener('submit', submitHandler));
  detachHandlers.push(() => list.removeEventListener('click', sessionHandler));
  detachHandlers.push(() => suggest.removeEventListener('click', suggestionHandler));
  detachHandlers.push(() => stream.removeEventListener('click', feedbackHandler));
  detachHandlers.push(() => newSessionBtn.removeEventListener('click', newSessionHandler));
  detachHandlers.push(() => languageSelect.removeEventListener('change', languageHandler));
}

export const CHAT_PAGE_STYLES = `
  .chat-page {
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: 12px;
    min-height: calc(100vh - 220px);
  }

  .chat-page__sidebar,
  .chat-page__main {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.12));
    background: var(--terminal-bg-panel, rgba(17, 24, 39, 0.75));
    border-radius: 8px;
  }

  .chat-page__sidebar { padding: 10px; }
  .chat-page__sidebar-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .chat-page__sidebar-head button,
  .chat-page__composer button,
  .chat-page__suggestions button,
  .chat-page__feedback button,
  .chat-page__session {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.18));
    background: var(--terminal-bg-elevated, rgba(255,255,255,0.04));
    color: var(--terminal-text, #e5e7eb);
    border-radius: 6px;
    font: inherit;
  }

  .chat-page__sessions {
    display: grid;
    gap: 6px;
    max-height: calc(100vh - 320px);
    overflow: auto;
  }

  .chat-page__session {
    text-align: left;
    padding: 8px;
    font-size: 12px;
  }

  .chat-page__session--active {
    border-color: var(--terminal-accent, #f97316);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--terminal-accent, #f97316) 35%, transparent);
  }

  .chat-page__main {
    display: grid;
    grid-template-rows: auto 1fr auto;
    min-height: calc(100vh - 220px);
  }

  .chat-page__header {
    padding: 12px;
    border-bottom: 1px solid var(--terminal-line, rgba(255,255,255,0.12));
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
  }

  .chat-page__header h2 { margin: 0; font-size: 18px; }
  .chat-page__header p { margin: 4px 0 0; color: var(--terminal-text-muted, #9ca3af); font-size: 12px; }

  .chat-page__suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
  }

  .chat-page__controls {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--terminal-text-muted, #9ca3af);
  }

  .chat-page__controls select {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.18));
    background: var(--terminal-bg-elevated, rgba(255,255,255,0.04));
    color: var(--terminal-text, #e5e7eb);
    border-radius: 6px;
    padding: 4px 6px;
    font: inherit;
  }

  .chat-page__suggestions button {
    padding: 6px 8px;
    font-size: 11px;
  }

  .chat-page__stream {
    overflow: auto;
    padding: 12px;
    display: grid;
    gap: 10px;
    align-content: start;
  }

  .chat-page__empty {
    color: var(--terminal-text-muted, #9ca3af);
    font-size: 13px;
    padding: 10px;
  }

  .chat-page__msg {
    display: grid;
    gap: 6px;
  }

  .chat-page__msg--user { justify-items: end; }
  .chat-page__msg--assistant { justify-items: start; }

  .chat-page__bubble {
    max-width: min(860px, 90%);
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.14));
    background: rgba(255,255,255,0.03);
    padding: 10px;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
  }

  .chat-page__msg--user .chat-page__bubble {
    border-color: color-mix(in srgb, var(--terminal-accent, #f97316) 40%, transparent);
  }

  .chat-page__bubble--streaming {
    border-style: dashed;
    opacity: 0.95;
  }

  .chat-page__cards {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .chat-page__card {
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.12));
    background: rgba(255,255,255,0.02);
    padding: 8px;
    border-radius: 6px;
    min-width: 180px;
    max-width: 300px;
    font-size: 12px;
  }

  .chat-page__card small { color: var(--terminal-text-muted, #9ca3af); }

  .chat-page__feedback {
    display: flex;
    gap: 6px;
  }

  .chat-page__feedback button {
    padding: 4px 8px;
    font-size: 11px;
  }

  .chat-page__composer {
    border-top: 1px solid var(--terminal-line, rgba(255,255,255,0.12));
    padding: 10px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
  }

  .chat-page__composer textarea {
    width: 100%;
    resize: vertical;
    min-height: 56px;
    border-radius: 8px;
    border: 1px solid var(--terminal-line, rgba(255,255,255,0.18));
    background: var(--terminal-bg-elevated, rgba(255,255,255,0.04));
    color: var(--terminal-text, #e5e7eb);
    padding: 8px;
    font: inherit;
  }

  .chat-page__composer button {
    padding: 10px 16px;
    align-self: end;
  }

  @media (max-width: 900px) {
    .chat-page {
      grid-template-columns: 1fr;
    }

    .chat-page__sidebar {
      max-height: 220px;
      overflow: auto;
    }

    .chat-page__main {
      min-height: 70vh;
    }
  }
`;
